import React from "react";
import { motion } from "framer-motion";
import { Users, Target, Award, Heart } from "lucide-react";

const About: React.FC = () => {
  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for excellence in every project, delivering the highest quality solutions.",
    },
    {
      icon: Heart,
      title: "Passion",
      description: "Our passion for technology drives us to create innovative and impactful solutions.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "We believe in the power of teamwork and close collaboration with our clients.",
    },
    {
      icon: Award,
      title: "Innovation",
      description: "We stay ahead of the curve with cutting-edge technologies and creative approaches.",
    },
  ];

  const team = [
    {
      name: "Vikas Sharma",
      role: "CEO & Founder",
      image: "/male.png",
      description:
        "15+ years of experience in the tech industry, with a strong focus on mobile application development and expertise in backend systems and scalable architectures.",
    },
    {
      name: "Anju Ashok",
      role: "Lead Application Developer",
      image: "./female.png",
      description: "Technical expert web application development.",
    },
    {
      name: "Rupam Saini",
      role: "Lead Mobile Developer",
      image: "/male.png",
      description: "Tech expert mobile application development",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white section">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="mb-6 text-5xl font-bold lg:text-6xl text-dark-gray">About Codeflair</h1>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-medium-gray">
              We are a passionate team of developers, designers, and innovators dedicated to creating exceptional digital experiences that drive
              business growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="bg-white section">
        <div className="container">
          <div className="grid grid-cols-1 gap-12 items-center lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h2 className="mb-6 text-4xl font-bold text-dark-gray">Our Story</h2>
              <p className="mb-6 text-lg leading-relaxed text-medium-gray">
                Founded in 2020, Codeflair began with a simple mission: to make technology accessible and impactful for businesses of all sizes. We
                believe that every company deserves access to world-class digital solutions.
              </p>
              <p className="text-lg leading-relaxed text-medium-gray">
                Today, we've grown into a team of passionate professionals who combine technical expertise with creative innovation to deliver
                solutions that not only meet but exceed our clients' expectations.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <div className="p-8 rounded-2xl bg-gradient-primary shadow-heavy">
                <div className="p-6 bg-white rounded-xl shadow-medium">
                  <h3 className="mb-4 text-2xl font-bold text-dark-gray">Our Mission</h3>
                  <p className="mb-4 text-medium-gray">
                    To empower businesses with innovative technology solutions that drive growth and create lasting impact.
                  </p>
                  <h3 className="mb-4 text-2xl font-bold text-dark-gray">Our Vision</h3>
                  <p className="text-medium-gray">
                    To be the leading partner for businesses seeking to transform their digital presence and achieve their goals.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-light-gray">
        <div className="container">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-dark-gray">Our Values</h2>
            <p className="mx-auto max-w-2xl text-xl text-medium-gray">These core values guide everything we do and shape our culture.</p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="p-6 text-center bg-white rounded-xl transition-shadow shadow-medium hover:shadow-heavy">
                  <div className="flex justify-center mb-4">
                    <value.icon size={48} className="text-primary-blue" />
                  </div>
                  <h3 className="mb-3 text-xl font-semibold text-dark-gray">{value.title}</h3>
                  <p className="text-medium-gray">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-white section">
        <div className="container">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-4xl font-bold text-dark-gray">Meet Our Team</h2>
            <p className="mx-auto max-w-2xl text-xl text-medium-gray">The talented individuals behind our success.</p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="p-6 text-center bg-white rounded-xl transition-shadow shadow-medium hover:shadow-heavy">
                  <img src={member.image} alt={member.name} className="object-cover mx-auto mb-4 w-32 h-32 rounded-full" />
                  <h3 className="mb-2 text-xl font-semibold text-dark-gray">{member.name}</h3>
                  <p className="mb-3 font-medium text-primary-blue">{member.role}</p>
                  <p className="text-medium-gray">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
